import { isMetaClickIdentifier } from "@newform/constants";
import { z } from "zod";
import {
  canUseCreatorMetaPixel,
  readMetaClickIdentifiers,
  resolveMetaPixelId,
} from "./metaPixel";

const STORAGE_KEY = "newform.creatorAcquisition.v1";
const MAX_AGE_MS = 90 * 24 * 60 * 60 * 1000;
const attributionValue = z.string().trim().min(1).max(512).optional();
const contextSchema = z.object({
  utmSource: attributionValue,
  utmMedium: attributionValue,
  utmCampaign: attributionValue,
  utmContent: attributionValue,
  utmTerm: attributionValue,
});
const identifierSchema = z.string().refine(isMetaClickIdentifier).optional();
const snapshotSchema = z.object({
  capturedAt: z.number().int().positive(),
  acquisitionContext: contextSchema,
  fbc: identifierSchema,
  fbp: identifierSchema,
});

type AcquisitionSnapshot = z.infer<typeof snapshotSchema>;
let memorySnapshot: AcquisitionSnapshot | null = null;

function readSnapshot(): AcquisitionSnapshot | null {
  try {
    const raw = window.localStorage.getItem(STORAGE_KEY);
    const parsed = raw ? snapshotSchema.safeParse(JSON.parse(raw)) : null;
    if (parsed?.success) memorySnapshot = parsed.data;
  } catch {
    // Storage may be unavailable. Attribution must never block the funnel.
  }
  if (
    memorySnapshot &&
    memorySnapshot.capturedAt <= Date.now() &&
    Date.now() - memorySnapshot.capturedAt < MAX_AGE_MS
  ) {
    return memorySnapshot;
  }
  return null;
}

function queryAttribution(params: URLSearchParams) {
  const read = (key: string) => {
    const value = attributionValue.safeParse(params.get(key) ?? undefined);
    return value.success ? value.data : undefined;
  };
  return {
    utmSource: read("utm_source"),
    utmMedium: read("utm_medium"),
    utmCampaign: read("utm_campaign"),
    utmContent: read("utm_content"),
    utmTerm: read("utm_term"),
  };
}

/** Keep ad identity outside navigation URLs, including an OAuth round trip. */
export function captureCreatorAcquisition(): AcquisitionSnapshot | null {
  if (
    !canUseCreatorMetaPixel() ||
    !resolveMetaPixelId(window.location.hostname)
  ) {
    return null;
  }
  const previous = readSnapshot();
  const params = new URLSearchParams(window.location.search);
  const acquisitionContext = queryAttribution(params);
  const hasCampaign = Object.values(acquisitionContext).some(Boolean);
  const cookies = readMetaClickIdentifiers();
  const clickId = params.get("fbclid");
  const now = Date.now();
  // Derive fbc only from a real landing fbclid, using Meta's documented format.
  // Capturing before fbevents loads avoids losing it on a fast move to Apply.
  const candidate = clickId ? `fb.1.${now}.${clickId}` : undefined;
  const savedFbc = [cookies.fbc, previous?.fbc]
    .filter((value): value is string => {
      if (!value) return false;
      const age = now - Number(value.split(".")[2]);
      return age >= 0 && age < MAX_AGE_MS;
    })
    .sort(
      (left, right) => Number(right.split(".")[2]) - Number(left.split(".")[2]),
    )[0];
  const currentFbc =
    candidate && isMetaClickIdentifier(candidate)
      ? savedFbc?.split(".").slice(3).join(".") === clickId
        ? savedFbc
        : candidate
      : undefined;
  const snapshot: AcquisitionSnapshot = {
    capturedAt:
      currentFbc || hasCampaign || !previous ? now : previous.capturedAt,
    acquisitionContext:
      currentFbc || hasCampaign
        ? acquisitionContext
        : (previous?.acquisitionContext ?? {}),
    fbp: cookies.fbp ?? previous?.fbp,
    fbc: currentFbc ?? savedFbc,
  };
  memorySnapshot = snapshot;
  try {
    window.localStorage.setItem(STORAGE_KEY, JSON.stringify(snapshot));
  } catch {
    // The in-memory snapshot still covers same-document navigation.
  }
  if (currentFbc) {
    try {
      const maxAgeSeconds = Math.floor(
        (Number(currentFbc.split(".")[2]) + MAX_AGE_MS - now) / 1000,
      );
      // biome-ignore lint/suspicious/noDocumentCookie: set synchronously before the SDK starts, including browsers without Cookie Store.
      document.cookie = `_fbc=${encodeURIComponent(currentFbc)}; Path=/; Max-Age=${maxAgeSeconds}; SameSite=Lax; Secure`;
    } catch {
      // Cookie access and storage access can be denied independently.
    }
  }
  return snapshot;
}

export function readCreatorAcquisition() {
  const snapshot = captureCreatorAcquisition();
  return snapshot
    ? {
        fbp: snapshot.fbp,
        fbc: snapshot.fbc,
        acquisitionContext: snapshot.acquisitionContext,
      }
    : {};
}

export function creatorSubmissionMetaContext() {
  if (!canUseCreatorMetaPixel() || window.location.pathname !== "/one/submit")
    return {};
  const { fbp, fbc, acquisitionContext } = readCreatorAcquisition();
  return {
    metaBrowser: {
      pageUrl: `${window.location.origin}/one/submit`,
      fbp,
      fbc,
    },
    acquisitionContext,
  };
}
